// src/Component/AuthNotificationDemo.js
import React, { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import notificationService from '../services/notificationService';
import config from '../config';

const AuthNotificationDemo = () => {
  const [logs, setLogs] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isSupported, isEnabled, fcmToken } = useNotifications();

  // Add log entry
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = !!(config.token && config.user);
      setIsAuthenticated(authStatus);
      addLog(`Authentication status: ${authStatus ? 'Logged In' : 'Not Logged In'}`, authStatus ? 'success' : 'warning');
    };

    checkAuth();
    // Check periodically for auth changes
    const interval = setInterval(checkAuth, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Test notification initialization for non-authenticated users
  const testGuestNotification = async () => {
    addLog('Testing notification for guest user...', 'info');
    try {
      const result = await notificationService.initialize();
      if (result) {
        addLog('✅ Guest notification initialized (no API call made)', 'success');
      } else {
        addLog('❌ Guest notification failed', 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
  };

  // Test notification initialization for authenticated users
  const testAuthenticatedNotification = async () => {
    addLog('Testing notification for authenticated user...', 'info');
    try {
      const result = await notificationService.initializeAfterLogin();
      if (result) {
        addLog('✅ Authenticated notification initialized (API call made)', 'success');
      } else {
        addLog('❌ Authenticated notification failed', 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      default: return '#17a2b8';
    }
  };

  return (
    <div style={{ padding: '20px', border: '1px solid #ddd', margin: '20px', borderRadius: '8px' }}>
      <h3>Authentication & Notification Demo</h3>
      
      {/* Status Display */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
        <h5>Current Status:</h5>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li>Browser Support: {isSupported ? '✅' : '❌'}</li>
          <li>Notifications Enabled: {isEnabled ? '✅' : '❌'}</li>
          <li>User Authenticated: {isAuthenticated ? '✅' : '❌'}</li>
          <li>FCM Token: {fcmToken ? '✅ Generated' : '❌ None'}</li>
        </ul>
      </div>

      {/* Test Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={testGuestNotification}
          style={{
            padding: '10px 15px',
            marginRight: '10px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Guest Notification
        </button>
        
        <button 
          onClick={testAuthenticatedNotification}
          disabled={!isAuthenticated}
          style={{
            padding: '10px 15px',
            marginRight: '10px',
            backgroundColor: isAuthenticated ? '#007bff' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isAuthenticated ? 'pointer' : 'not-allowed'
          }}
        >
          Test Auth Notification
        </button>

        <button 
          onClick={clearLogs}
          style={{
            padding: '10px 15px',
            backgroundColor: '#ffc107',
            color: 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Logs
        </button>
      </div>

      {/* Logs Display */}
      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
          Debug Logs:
        </div>
        <div style={{ padding: '10px' }}>
          {logs.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No logs yet. Test the buttons above.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index} style={{ 
                marginBottom: '5px', 
                fontSize: '14px',
                color: getLogColor(log.type)
              }}>
                <span style={{ color: '#666' }}>[{log.timestamp}]</span> {log.message}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
        <strong>How to test:</strong>
        <ol>
          <li><strong>Guest Test:</strong> Click "Test Guest Notification" - should generate FCM token but NOT call API</li>
          <li><strong>Login:</strong> Log in to your account using the login page</li>
          <li><strong>Auth Test:</strong> Click "Test Auth Notification" - should generate token AND call update-fcm-token API</li>
          <li><strong>Logout:</strong> Log out and see tokens get cleaned up</li>
        </ol>
        <p><strong>Note:</strong> Check browser console and network tab for detailed logs and API calls.</p>
      </div>
    </div>
  );
};

export default AuthNotificationDemo;
